/**
 * PathKit Control Grid — pure builder.
 *
 * Takes (capabilities config, manual overrides, vitest JSON reporter output)
 * and returns a deterministic ControlGridRow[] plus a list of warnings about
 * mappings that did not resolve to any assertion.
 *
 * No I/O, no React. Trivially unit-testable.
 *
 * Vitest's `--reporter=json` emits a Jest-compatible shape. The fields we
 * rely on are documented inline; if Vitest changes the schema, this file is
 * the single point of update.
 */

export type RowStatus = 'healthy' | 'watch' | 'broken' | 'unknown';
export type RowSource = 'tests' | 'override' | 'mixed' | 'none';
export type Priority = 'P0' | 'P1' | 'P2';

export interface CapabilityTestMapping {
  /** Repo-relative path, e.g. "tests/integration/analyzeSOWDialog.test.tsx". */
  file: string;
  /**
   * Optional top-level describe block name. When set, only assertions whose
   * outermost ancestor title equals this string count toward the capability.
   */
  describe?: string;
}

export interface CapabilityConfig {
  id: string;
  label: string;
  owner?: string;
  priority?: Priority;
  tests: CapabilityTestMapping[];
  notes?: string;
  /**
   * Optional checklist of human-verifiable assertions for capabilities that
   * cannot (yet) be tested automatically. Surfaced in the System Admin
   * Control Grid as a "Run manual checks" button that opens a dialog,
   * computes a status from pass/fail tallies, and produces a manual override
   * that the operator pastes into manual-overrides.json.
   */
  manualChecks?: string[];
}

export interface CapabilitiesFile {
  version: number;
  thresholds?: Partial<Thresholds>;
  capabilities: CapabilityConfig[];
}

export interface ManualOverride {
  capabilityId: string;
  status: RowStatus;
  confidence?: number;
  note?: string;
  /** ISO 8601. After this instant the override is ignored. */
  expiresAt?: string;
}

export interface OverridesFile {
  version: number;
  overrides: ManualOverride[];
}

export interface VitestAssertion {
  ancestorTitles: string[];
  title: string;
  fullName?: string;
  status: 'passed' | 'failed' | 'pending' | 'skipped' | 'todo';
  duration?: number;
  failureMessages?: string[];
}

export interface VitestTestResult {
  /** Absolute path to the test file. */
  name: string;
  startTime?: number;
  endTime?: number;
  status?: 'passed' | 'failed';
  assertionResults: VitestAssertion[];
}

export interface VitestRun {
  startTime?: number;
  numTotalTests?: number;
  numPassedTests?: number;
  numFailedTests?: number;
  numPendingTests?: number;
  testResults: VitestTestResult[];
}

export interface Thresholds {
  healthyMinConfidence: number;
  watchMinConfidence: number;
  brokenMaxConfidence: number;
  /** A run older than this many ms is treated as missing. */
  staleAfterMs: number;
}

export const DEFAULT_THRESHOLDS: Thresholds = {
  healthyMinConfidence: 0.9,
  watchMinConfidence: 0.6,
  brokenMaxConfidence: 0.5,
  staleAfterMs: 24 * 60 * 60 * 1000,
};

export interface ControlGridRow {
  id: string;
  label: string;
  priority: Priority;
  status: RowStatus;
  confidence: number;
  testsTotal: number;
  testsPassed: number;
  testsFailed: number;
  testsSkipped: number;
  failedTestNames: string[];
  lastRunAt: string | null;
  source: RowSource;
  nextAction: string | null;
  notes?: string;
}

export interface BuilderWarning {
  capabilityId: string;
  mapping: CapabilityTestMapping;
  reason: 'no-matching-file' | 'no-matching-describe';
}

export interface BuildResult {
  rows: ControlGridRow[];
  warnings: BuilderWarning[];
  lastRunAt: string | null;
  staleRun: boolean;
}

interface BuildOptions {
  /** Override "now" for deterministic tests. Defaults to `Date.now()`. */
  now?: number;
}

/**
 * Match a mapping's repo-relative file against a Vitest reporter `name`
 * (which is absolute). Suffix match on path separator boundaries so
 * "addSOWDialog.test.tsx" never matches "AddSOWDialog.test.tsx" by accident
 * across systems with different cwd lengths.
 */
function fileMatches(mappingFile: string, reporterName: string): boolean {
  const normalized = reporterName.replace(/\\/g, '/');
  if (normalized === mappingFile) return true;
  return normalized.endsWith('/' + mappingFile);
}

function aggregateForCapability(
  cap: CapabilityConfig,
  run: VitestRun | null,
  warnings: BuilderWarning[],
): {
  total: number;
  passed: number;
  failed: number;
  skipped: number;
  failedNames: string[];
  hadAnyMappedFile: boolean;
} {
  let total = 0;
  let passed = 0;
  let failed = 0;
  let skipped = 0;
  const failedNames: string[] = [];
  let hadAnyMappedFile = false;

  if (!run || cap.tests.length === 0) {
    return { total, passed, failed, skipped, failedNames, hadAnyMappedFile };
  }

  for (const mapping of cap.tests) {
    const matchingFiles = run.testResults.filter((tr) =>
      fileMatches(mapping.file, tr.name),
    );

    if (matchingFiles.length === 0) {
      warnings.push({
        capabilityId: cap.id,
        mapping,
        reason: 'no-matching-file',
      });
      continue;
    }

    hadAnyMappedFile = true;
    let assertionsHit = 0;

    for (const file of matchingFiles) {
      for (const a of file.assertionResults) {
        if (mapping.describe) {
          // Vitest's reporter prepends an empty "" root entry, so the
          // outermost named describe lives at index 1 in some versions and
          // index 0 in others. Match on any non-empty ancestor title so the
          // mapping survives both shapes and arbitrary nesting depth.
          const ancestors = a.ancestorTitles.filter((t) => t.length > 0);
          if (!ancestors.includes(mapping.describe)) continue;
        }
        assertionsHit += 1;
        total += 1;

        if (a.status === 'passed') {
          passed += 1;
        } else if (a.status === 'failed') {
          failed += 1;
          const fullName =
            a.fullName ?? [...a.ancestorTitles, a.title].join(' > ');
          failedNames.push(fullName);
        } else {
          // pending / skipped / todo all count as skipped for our purposes
          skipped += 1;
        }
      }
    }

    if (mapping.describe && assertionsHit === 0) {
      warnings.push({
        capabilityId: cap.id,
        mapping,
        reason: 'no-matching-describe',
      });
    }
  }

  return { total, passed, failed, skipped, failedNames, hadAnyMappedFile };
}

function deriveStatus(
  total: number,
  passed: number,
  failed: number,
  thresholds: Thresholds,
): { status: RowStatus; confidence: number } {
  if (total === 0) return { status: 'unknown', confidence: 0 };
  const confidence = passed / total;

  if (failed === 0 && confidence >= thresholds.healthyMinConfidence) {
    return { status: 'healthy', confidence };
  }
  if (failed === 0 && confidence >= thresholds.watchMinConfidence) {
    return { status: 'watch', confidence };
  }
  if (failed > 0 && confidence >= thresholds.watchMinConfidence) {
    return { status: 'watch', confidence };
  }
  return { status: 'broken', confidence };
}

function deriveNextAction(
  status: RowStatus,
  failedNames: string[],
  label: string,
  override: ManualOverride | null,
): string | null {
  if (override?.note) return override.note;
  switch (status) {
    case 'broken':
      return failedNames.length > 0
        ? `investigate: ${failedNames[0]}`
        : `fix ${label}`;
    case 'watch':
      return failedNames.length > 0
        ? `triage: ${failedNames[0]}`
        : `monitor ${label}`;
    case 'unknown':
      return `add coverage for ${label}`;
    case 'healthy':
    default:
      return null;
  }
}

function pickActiveOverride(
  capId: string,
  overrides: ManualOverride[],
  now: number,
): ManualOverride | null {
  for (const o of overrides) {
    if (o.capabilityId !== capId) continue;
    if (o.expiresAt) {
      const exp = Date.parse(o.expiresAt);
      if (!Number.isNaN(exp) && exp <= now) continue;
    }
    return o;
  }
  return null;
}

export function buildControlGrid(
  capabilitiesFile: CapabilitiesFile,
  overridesFile: OverridesFile | null,
  run: VitestRun | null,
  options: BuildOptions = {},
): BuildResult {
  const now = options.now ?? Date.now();
  const thresholds: Thresholds = {
    ...DEFAULT_THRESHOLDS,
    ...(capabilitiesFile.thresholds ?? {}),
  };

  const lastRunAt =
    run?.startTime && Number.isFinite(run.startTime)
      ? new Date(run.startTime).toISOString()
      : null;
  const staleRun =
    lastRunAt !== null &&
    now - (run!.startTime as number) > thresholds.staleAfterMs;

  // A stale run is treated as no run at all for aggregation so capabilities
  // do not display fossilised pass/fail data. The lastRunAt timestamp is
  // still surfaced on the result so the UI can show a "stale" badge.
  const effectiveRun = staleRun ? null : run;

  const warnings: BuilderWarning[] = [];
  const overrides = overridesFile?.overrides ?? [];

  const rows: ControlGridRow[] = capabilitiesFile.capabilities.map((cap) => {
    const agg = aggregateForCapability(cap, effectiveRun, warnings);
    const derived = deriveStatus(
      agg.total,
      agg.passed,
      agg.failed,
      thresholds,
    );

    const override = pickActiveOverride(cap.id, overrides, now);

    let status = derived.status;
    let confidence = derived.confidence;
    let source: RowSource;

    if (override) {
      status = override.status;
      if (typeof override.confidence === 'number') {
        confidence = override.confidence;
      }
      source = agg.total > 0 ? 'mixed' : 'override';
    } else {
      source = agg.total > 0 ? 'tests' : 'none';
    }

    const nextAction = deriveNextAction(
      status,
      agg.failedNames,
      cap.label,
      override,
    );

    return {
      id: cap.id,
      label: cap.label,
      priority: cap.priority ?? 'P2',
      status,
      confidence,
      testsTotal: agg.total,
      testsPassed: agg.passed,
      testsFailed: agg.failed,
      testsSkipped: agg.skipped,
      failedTestNames: agg.failedNames,
      lastRunAt,
      source,
      nextAction,
      notes: cap.notes,
    };
  });

  return { rows, warnings, lastRunAt, staleRun };
}
